import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import "./UserActivityDashboard.css";

export const UserActivityDashboard = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_API_DOMAIN + "/listUserEmails",
          {
            headers: {
              authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
        setUsers(response.data.users);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch user data");
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    setDetailsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_DOMAIN + "/getAllUsersData",
        { email: user.email },
        {
          headers: {
            authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      setUserDetails(response.data);
    } catch (err) {
      setError("Failed to fetch user details");
    }
    setDetailsLoading(false);
  };

  const handleKeyPress = (event, user) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleUserSelect(user);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleConversationSelect = (conversationId) => {
    if (selectedConversation === conversationId) {
      setSelectedConversation(null);
    } else {
      setSelectedConversation(conversationId);
    }
  };

  if (loading) return <div className="loading">Loading users...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="dashboard-container">
      <div className="user-list-container">
        <h1>Users</h1>
        <div className="user-list">
          {users.map((user) => (
            <button
              key={user.userId}
              className={`user-item ${
                selectedUser?.userId === user.userId ? "selected" : ""
              }`}
              onClick={() => handleUserSelect(user)}
              onKeyPress={(e) => handleKeyPress(e, user)}
              aria-pressed={selectedUser?.userId === user.userId}
            >
              <div className="user-info">
                <span className="user-name">{user.name}</span>
                <span className="user-email">{user.email}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="details-pane">
        <div className="details-header">
          <h2>
            {selectedUser
              ? `${selectedUser.name}'s Activity`
              : "Select a user to view details"}
          </h2>
        </div>

        {selectedUser &&
          (detailsLoading ? (
            <div className="loading">Loading user details...</div>
          ) : userDetails ? (
            <div className="details-content">
              <div className="details-section">
                <h3>Reports ({userDetails.reports.length})</h3>
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetails.reports.map((report) => (
                      <tr key={report.id}>
                        <td>
                          <Link
                            to={`/jam?reportId=${report.id}&isShare=true`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {report.title}
                          </Link>
                        </td>
                        <td>{formatDate(report.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="details-section">
                <h3>Conversations ({userDetails.conversations.length})</h3>
                <table className="conversations-table">
                  <thead>
                    <tr>
                      <th>Query</th>
                      <th>Time</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetails.conversations.map((conv) => (
                      <React.Fragment key={conv.conversation_id}>
                        <tr>
                          <td>{conv.title}</td>
                          <td>{formatDate(conv.timestamp)}</td>
                          <td>
                            <button
                              className="toggle-messages-btn"
                              onClick={() => handleConversationSelect(conv.id)}
                            >
                              {selectedConversation === conv.id
                                ? "Hide Messages"
                                : "Show Messages"}
                            </button>
                          </td>
                        </tr>
                        {selectedConversation === conv.id && (
                          <tr>
                            <td colSpan="3">
                              <div className="message-history">
                                {conv.messages && conv.messages.length > 0 ? (
                                  conv.messages.map((message) => (
                                    <React.Fragment key={message.message_id}>
                                      {message.outgoing_message && (
                                        <div className="message outgoing">
                                          <strong>User</strong>
                                          <p>{message.outgoing_message}</p>
                                        </div>
                                      )}
                                      {message.incoming_message && (
                                        <div className="message incoming">
                                          <strong>Assistant</strong>
                                          <p>{message.incoming_message}</p>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  ))
                                ) : (
                                  <div className="no-messages">
                                    No messages found for this conversation
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="error">No details available</div>
          ))}
      </div>
    </div>
  );
};

export default UserActivityDashboard;
